import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiService } from "../services/api";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ColorModeIconDropdown from "../theme/ColorModeIconDropdown";

const PaymentContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100vh",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    }),
  },
}));

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"verifying" | "success" | "error">(
    "verifying",
  );
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const sessionId = searchParams.get("session_id");

    if (!sessionId) {
      setStatus("error");
      setError("No session ID found");
      return;
    }

    const verifySession = async (id: string) => {
      try {
        const verifyResponse = await apiService.verifyCheckoutSession(id);

        if (!verifyResponse.data) {
          throw new Error("No session data returned");
        }

        // Poll until the webhook has created the account (max ~30 seconds)
        const maxAttempts = 15;
        const pollInterval = 2000;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval));

          try {
            const readyResponse = await apiService.checkAccountReady(id);
            if (readyResponse.data?.ready) {
              setStatus("success");
              setTimeout(() => {
                navigate("/login", { replace: true });
              }, 4000);
              return;
            }
          } catch {
            // keep polling
          }
        }

        // Payment succeeded but account setup took too long — still show success
        // since the webhook will complete eventually
        setStatus("success");
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 4000);
      } catch (err: any) {
        console.error("Payment verification error:", err);
        setStatus("error");
        setError(
          err.response?.data?.message ||
            "Failed to verify payment. Please contact support.",
        );
      }
    };

    verifySession(sessionId);
  }, [searchParams, navigate]);

  if (status === "verifying") {
    return (
      <>
        <CssBaseline enableColorScheme />
        <PaymentContainer
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            sx={{
              position: "absolute",
              top: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
            }}
          >
            <ColorModeIconDropdown />
          </Box>
          <Card variant="outlined" sx={{ maxWidth: 450, width: "100%" }}>
            <CardContent sx={{ textAlign: "center", p: 4 }}>
              <CircularProgress size={64} sx={{ mb: 3 }} />
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                Verifying Payment...
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we set up your account.
              </Typography>
            </CardContent>
          </Card>
        </PaymentContainer>
      </>
    );
  }

  if (status === "error") {
    return (
      <>
        <CssBaseline enableColorScheme />
        <PaymentContainer
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            sx={{
              position: "absolute",
              top: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
            }}
          >
            <ColorModeIconDropdown />
          </Box>
          <Card variant="outlined" sx={{ maxWidth: 450, width: "100%" }}>
            <CardContent sx={{ textAlign: "center", p: 4 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "error.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <ErrorIcon sx={{ fontSize: 40, color: "error.main" }} />
              </Box>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                Verification Failed
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {error}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/register")}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </PaymentContainer>
      </>
    );
  }

  return (
    <>
      <CssBaseline enableColorScheme />
      <PaymentContainer
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          sx={{
            position: "absolute",
            top: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
          }}
        >
          <ColorModeIconDropdown />
        </Box>
        <Card variant="outlined" sx={{ maxWidth: 450, width: "100%" }}>
          <CardContent sx={{ textAlign: "center", p: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "success.light",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
            </Box>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              Payment Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your account has been created. You can now log in with the
              credentials you provided.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate("/login")}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </PaymentContainer>
    </>
  );
};

export default PaymentSuccess;
